import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ExternalLink, LoadMore, Segmented } from '../components/data';
import { Banner, Button, ErrorState, Loading, PageHeader } from '../components/ui';
import { errorMessage } from '../lib/api';
import { dateTime } from '../lib/format';
import { fetchFeatureRequests, qk, setFeatureRequestHandled, type FeatureRequest } from '../queries/admin';

type Filter = '0' | '1' | '';

const OPTIONS: Array<{ value: Filter; label: string }> = [
  { value: '0', label: 'Open' },
  { value: '1', label: 'Handled' },
  { value: '', label: 'All' },
];

function RequestCard({ request }: { request: FeatureRequest }) {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: (handled: boolean) => setFeatureRequestHandled(request.id, handled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'feature-requests'] }),
  });
  const handled = request.handledAt !== null;

  return (
    <article className="card px-4 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-h4 text-chalk">{request.title}</h3>
          <p className="mt-1 text-caption text-chalk-faint">
            {request.contact} · {dateTime(request.createdAt)} · via {request.source}
            {handled ? ` · handled ${dateTime(request.handledAt)}` : ''}
          </p>
        </div>
        <Button
          size="sm"
          variant={handled ? 'ghost' : 'secondary'}
          busy={toggle.isPending}
          onClick={() => toggle.mutate(!handled)}
          className="shrink-0"
        >
          {handled ? 'Reopen' : 'Mark handled'}
        </Button>
      </div>
      <p className="mt-3 whitespace-pre-wrap text-body text-chalk-dim">{request.body}</p>
      {request.videoUrl ? (
        <p className="mt-2 text-caption">
          <ExternalLink href={request.videoUrl}>Watch the video they sent</ExternalLink>
        </p>
      ) : null}
      {toggle.isError ? (
        <div className="mt-3">
          <Banner tone="danger">{errorMessage(toggle.error)}</Banner>
        </div>
      ) : null}
    </article>
  );
}

export function RequestsPage() {
  const [filter, setFilter] = useState<Filter>('0');

  const requests = useInfiniteQuery({
    queryKey: qk.featureRequests(filter),
    queryFn: ({ pageParam }) => fetchFeatureRequests(filter, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const rows = requests.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title="Feature requests"
        subtitle="What people asked for on the marketing site."
        actions={<Segmented value={filter} onChange={setFilter} options={OPTIONS} />}
      />

      {requests.isPending ? (
        <Loading />
      ) : requests.isError ? (
        <ErrorState message={errorMessage(requests.error)} onRetry={() => void requests.refetch()} />
      ) : rows.length === 0 ? (
        <p className="card px-4 py-10 text-center text-body text-chalk-faint">
          {filter === '0' ? 'Nothing waiting. Inbox zero.' : 'Nothing here.'}
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </div>
      )}
      <LoadMore query={requests} />
    </>
  );
}
